import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreateTask, TaskPriority, TASK_PRIORITY_LABEL } from "@/api/tasks";
import { useClients, displayName } from "@/api/clients";
import { useUsers } from "@/api/users";
import { getApiErrorMessage } from "@/api/client";

const PRIORITY_OPTIONS = Object.keys(TASK_PRIORITY_LABEL) as TaskPriority[];

export default function CreateTaskModal({
  clientId: fixedClientId,
  onClose,
}: {
  clientId?: string;
  onClose: () => void;
}) {
  const createTask = useCreateTask();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("MEDIA");
  const [dueDate, setDueDate] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  const [assignedToUserId, setAssignedToUserId] = useState("");

  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });
  const { data: usersData } = useUsers({ pageSize: 50 });

  const effectiveClientId = fixedClientId ?? selectedClient?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createTask.mutateAsync({
        clientId: effectiveClientId,
        title,
        description: description || undefined,
        priority,
        dueDate: dueDate || undefined,
        assignedToUserId: assignedToUserId || undefined,
      });
      toast.success("Tarea creada correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nueva tarea</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Título</label>
            <input className="input-field mt-1" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Descripción</label>
            <textarea className="input-field mt-1" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          {!fixedClientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente (opcional, para tareas internas dejar en blanco)</label>
              {selectedClient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span>{selectedClient.name}</span>
                  <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => setSelectedClient(null)}>
                    Quitar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input-field mt-1"
                    placeholder="Buscar cliente por nombre o documento..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                  />
                  {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {clientResults.data.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClient({ id: c.id, name: displayName(c) });
                            setClientSearch("");
                          }}
                        >
                          {displayName(c)}
                          <span className="ml-1 text-xs text-gray-400">
                            {c.documentType} {c.documentNumber}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Prioridad</label>
              <select className="input-field mt-1" value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {TASK_PRIORITY_LABEL[p]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha límite</label>
              <input type="date" className="input-field mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Responsable</label>
            <select className="input-field mt-1" value={assignedToUserId} onChange={(e) => setAssignedToUserId(e.target.value)}>
              <option value="">Sin asignar</option>
              {usersData?.data.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createTask.isPending}>
              {createTask.isPending ? "Creando..." : "Crear tarea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
